import { useEffect, useState } from "react"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"
import { createSearchParams, Outlet, useNavigate } from "react-router-dom"
import { observer, useLocalObservable } from "mobx-react"
import { Col, Flex, Row } from "antd"
import { choose } from "choose-matching"

import { notify } from "@src/app/components/notify"
import { AuthLayout } from "@src/app/layouts/AuthLayout"
import { paths } from "@src/app/paths"
import uiStore from "@src/app/uiStore"
import { ReCaptchaProvider } from "@src/pages/auth"
import { REGISTRATION_STEPS } from "@src/pages/registration/constants"
import { RegistrationAlreadyHas } from "@src/pages/registration/ui/RegistrationAlredyHas"
import { RegistrationFormAuth } from "@src/pages/registration/ui/RegistrationFormAuth"
import { RegistrationFormSelfInfo } from "@src/pages/registration/ui/RegistrationFormSelfInfo"
import { RegistrationHeader } from "@src/pages/registration/ui/RegistrationHeader"
import {
  Response,
  SignupDetailsRequest,
  SignupDetailsRequestUserTypeEnum,
  SignupDetailsRequestUtms,
} from "@src/shared/api/generated"
import { useBreakpoint } from "@src/shared/hooks/useBreakpoints"
import { useCookie } from "@src/shared/hooks/useCookies"
import { useUserUtm } from "@src/shared/hooks/useUserUtm"
import { RegistrationAuthRequest, RegistrationStore } from "./store"

type Props = {
  forceUserType?: SignupDetailsRequestUserTypeEnum
}

const RegistrationPageContent = observer(({ forceUserType }: Props) => {
  const { utm } = useUserUtm()
  const nav = useNavigate()
  const [step, setStep] = useState(REGISTRATION_STEPS.AUTH)
  const store = useLocalObservable(() => new RegistrationStore())
  const { isLoading, registration, setForceUserType, state } = store
  const { executeRecaptcha } = useGoogleReCaptcha()
  const refId = new URLSearchParams(window.location.search).get("id")
  const { smallerThanLg } = useBreakpoint()

  useEffect(() => {
    if (refId && forceUserType) {
      nav({
        pathname: paths.registration,
        search: `?${createSearchParams({ id: refId })}`,
      })
    }

    if (forceUserType) {
      setForceUserType(forceUserType)
    }

    if (refId) {
      document.cookie = `referral_link_id=${refId}; max-age=86400;`
    }
  }, [])

  const onAuthFormSubmit = async (values: RegistrationAuthRequest) => {
    if (!executeRecaptcha) {
      throw new Error("reCAPTCHA is not ready")
    }

    const token = await executeRecaptcha("register")

    return registration(values, token).then((res: any) => {
      if (res) {
        setStep(REGISTRATION_STEPS.SELF_INFO)
      }
    })
  }

  const onSelfInfoSubmit = async (values: SignupDetailsRequest) => {
    if (!executeRecaptcha) {
      throw new Error("reCAPTCHA is not ready")
    }

    const token = await executeRecaptcha("register")

    return registration(
      {
        ...values,
        ...((utm ? { utms: utm } : {}) as SignupDetailsRequestUtms),
      },
      token,
    ).then((response: any) => {
      if (response) {
        const { data, message, success } = response as Response

        notify.success(message)

        nav({
          pathname: paths.auth,
          search: `?${createSearchParams({ changeText: "true" })}`,
        })
      }
    })
  }

  return (
    <>
      <AuthLayout>
        <Flex gap={16} style={{ width: "100%" }} vertical>
          <RegistrationHeader step={step} />

          <Flex gap={16} style={{ width: "100%" }} vertical>
            <Flex
              style={{
                backgroundColor: "white",
                borderRadius: 24,
                padding: smallerThanLg ? 6 : 32,
              }}
            >
              {choose(
                [
                  step === REGISTRATION_STEPS.AUTH,
                  <RegistrationFormAuth
                    forceUserType={forceUserType}
                    isLoading={isLoading}
                    onSubmit={onAuthFormSubmit}
                    refId={refId}
                  />,
                ],
                [
                  step === REGISTRATION_STEPS.SELF_INFO,
                  <RegistrationFormSelfInfo
                    isLoading={isLoading}
                    onSubmit={onSelfInfoSubmit}
                    userType={state.identity.user_type}
                  />,
                ],
              )}
            </Flex>

            {step === REGISTRATION_STEPS.AUTH && (
              <RegistrationAlreadyHas refId={refId} />
            )}
          </Flex>
        </Flex>
      </AuthLayout>

      <Outlet />
    </>
  )
})

export const RegistrationPage = (props: Props) => (
  <ReCaptchaProvider>
    <RegistrationPageContent {...props} />
  </ReCaptchaProvider>
)
